import { useState, useRef } from 'react';
import { Download, Upload, X, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

import { accountApi } from '@/api/account.api';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';

import { downloadAccountImportTemplate, parseAccountImportFile } from '@/utils/accountSheet';
import type { AccountImportRow } from '@/utils/accountSheet';
import type { CreateAccountRequest, AccountType, FinancialStatementGroup } from '@/types/account.types';

function deriveFinancialGroup(type: AccountType): FinancialStatementGroup {
  return (type === 'Asset' || type === 'Liability' || type === 'Equity')
    ? 'BALANCE_SHEET' : 'INCOME_STATEMENT';
}

interface Props {
  onImported: () => void;
  onClose:    () => void;
}

interface RowResult {
  rowNumber: number;
  code:      string;
  ok:        boolean;
  message:   string;
}

/**
 * Bulk-creating accounts from a spreadsheet (421271).
 *
 * There is no bulk-create endpoint, and none is needed — Create already exists and the import is
 * simple row-by-row (ticket decision), so this reads the sheet and calls it once per row,
 * resolving a parent by its code as it goes. Parent codes are resolved against a running cache of
 * codes created earlier in the same run first, then the server, so a sheet may list a header
 * account and its children in one upload as long as the header's row comes first.
 *
 * A row's failure (duplicate code, unknown parent, …) is reported against that row and does not
 * stop the rest — the same "simple row-by-row" choice means partial success is expected, not an
 * error state for the whole import.
 */
export const AccountsImportPanel = ({ onImported, onClose }: Props) => {
  const fileInput = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState<string | null>(null);
  const [rows,     setRows]     = useState<AccountImportRow[]>([]);
  const [problems, setProblems] = useState<{ rowNumber: number; message: string }[]>([]);
  const [results,  setResults]  = useState<RowResult[]>([]);

  const [busy,  setBusy]  = useState(false);
  const [error, setError] = useState<string | null>(null);

  const readFile = async (file: File) => {
    setError(null); setResults([]);
    setBusy(true);
    try {
      const parsed = await parseAccountImportFile(file);
      setRows(parsed.rows);
      setProblems(parsed.problems);
      setFileName(file.name);
    } catch (err) {
      setRows([]); setProblems([]); setFileName(null);
      setError(err instanceof Error ? err.message : 'That file could not be read.');
    } finally {
      setBusy(false);
    }
  };

  const errorMessage = (err: unknown): string => {
    const body = (err as { response?: { data?: { message?: string;
                   errors?: { message?: string }[] } } }).response?.data;
    return body?.errors?.[0]?.message ?? body?.message ?? 'The account was refused.';
  };

  const submit = async () => {
    if (rows.length === 0) { setError('There are no account rows to import.'); return; }

    setBusy(true); setError(null); setResults([]);
    const codeToId = new Map<string, string>();
    const outcome: RowResult[] = [];

    for (const row of rows) {
      let parentId: string | null = null;
      if (row.parentCode) {
        const parentKey = row.parentCode.toUpperCase();
        parentId = codeToId.get(parentKey) ?? null;
        if (!parentId) {
          try {
            const res = await accountApi.getByCode(row.parentCode);
            parentId = res.data.data?.id ?? null;
          } catch {
            outcome.push({ rowNumber: row.rowNumber, code: row.code, ok: false,
              message: `Parent code "${row.parentCode}" was not found.` });
            continue;
          }
        }
      }

      const accountType = row.accountType as AccountType;
      const payload: CreateAccountRequest = {
        code: row.code, name: row.name,
        accountType,
        accountNature: null,
        isPostingAccount: row.isPostingAccount,
        parentId, description: row.description, shortName: row.shortName,
        accountCategory: null, accountSubcategory: null, isControlAccount: false,
        currencyControl: null, allowMultiCurrency: true, taxApplicable: false,
        cashFlowCategory: null, financialStatementGroup: deriveFinancialGroup(accountType),
        revaluationRequired: false, isRetainedEarnings: false,
        costCenterMandatory: false, locationMandatory: false,
        projectMandatory: false, departmentMandatory: false,
        allowManualJournal: true, allowPaymentPosting: true, allowReceiptPosting: true,
        approvalRequired: false,
        postingRestriction: null, transactionRestriction: null,
        reportingGroup: null, reportingSequence: null,
        externalMappingCode: null, consolidationGroup: null,
        ifrsMappingCode: null, taxReportMappingCode: null, dashboardGroup: null,
        chequePayeeName: null, defaultChequeCrossing: null,
      };

      try {
        const res = await accountApi.create(payload);
        const id = res.data.data?.id;
        if (id) codeToId.set(row.code.toUpperCase(), id);
        outcome.push({ rowNumber: row.rowNumber, code: row.code, ok: true, message: 'Created.' });
      } catch (err) {
        outcome.push({ rowNumber: row.rowNumber, code: row.code, ok: false, message: errorMessage(err) });
      }
    }

    setResults(outcome);
    setBusy(false);
    if (outcome.some(r => r.ok)) onImported();
  };

  const okCount = results.filter(r => r.ok).length;

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Import accounts from Excel</h2>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={downloadAccountImportTemplate}
                  leftIcon={<Download className="w-3.5 h-3.5" />}>
            Download template
          </Button>
          <button onClick={onClose} aria-label="Close"
                  className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <div
          onClick={() => fileInput.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) readFile(f); }}
          className={cn(
            'border-2 border-dashed rounded-lg px-6 py-8 text-center cursor-pointer transition-colors',
            'border-slate-300 dark:border-slate-600 hover:border-teal-500 hover:bg-teal-50/40',
            'dark:hover:bg-teal-900/10',
          )}
        >
          <Upload className="w-6 h-6 mx-auto text-slate-400 mb-2" />
          <p className="text-sm text-slate-700 dark:text-slate-300">
            {fileName ?? 'Drop the .xlsx here, or click to choose'}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            It needs at least Code and Name columns. Download the template to see the full set.
          </p>
          <input ref={fileInput} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                 onChange={e => { const f = e.target.files?.[0]; if (f) readFile(f); e.target.value = ''; }} />
        </div>

        {busy && <div className="flex justify-center py-3"><Spinner /></div>}

        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {problems.length > 0 && (
          <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-300">
              <AlertTriangle className="w-4 h-4" />
              {problems.length} row{problems.length === 1 ? '' : 's'} could not be read and
              {problems.length === 1 ? ' is' : ' are'} not included
            </div>
            <ul className="mt-2 text-xs text-amber-700 dark:text-amber-400 space-y-0.5">
              {problems.slice(0, 8).map(p => (
                <li key={p.rowNumber}>Row {p.rowNumber}: {p.message}</li>
              ))}
              {problems.length > 8 && <li>… and {problems.length - 8} more</li>}
            </ul>
          </div>
        )}

        {rows.length > 0 && results.length === 0 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {rows.length} account{rows.length === 1 ? '' : 's'} ready to import.
            </p>
            <Button onClick={submit} disabled={busy} leftIcon={<Upload className="w-3.5 h-3.5" />}>
              Import {rows.length} account{rows.length === 1 ? '' : 's'}
            </Button>
          </div>
        )}

        {results.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {okCount} of {results.length} created.
            </p>
            <ul className="max-h-64 overflow-y-auto text-xs divide-y divide-slate-100 dark:divide-slate-700">
              {results.map(r => (
                <li key={r.rowNumber} className="flex items-center gap-2 py-1.5">
                  {r.ok
                    ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                    : <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
                  <span className="font-medium text-slate-700 dark:text-slate-200">Row {r.rowNumber} — {r.code}</span>
                  <span className={cn('truncate', r.ok ? 'text-slate-400' : 'text-red-600 dark:text-red-400')}>
                    {r.message}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
